import { createActions, OnSignal } from 'dreamstate';

import { Callable, Definable, Id, Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { firebaseConfig } from '../../../../configs/FirebaseConfig';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { EComposerSessionType } from '../../../../types/composer_session';
import { ELiveErrorCode } from '../../../../types/error';
import {
  IFirebaseDependencies,
  TFirebaseDatabase,
  TFirebaseReference,
} from '../../../../types/firebase';
import {
  EInteractionModule,
  IQnAQuestion,
  IQnASession,
  TInteractionHistoryConnection,
  TQnAQuestions,
  TQnAQuestionsReplies,
} from '../../../../types/interaction_tools';
import { IInteractionStoreInitialState } from '../../../../types/store';
import { LiveError } from '../../../../utils/error';
import {
  getDatabaseRef,
  getDatabaseRootRef,
  getRealtimeDatabase,
} from '../../../../utils/firebase/cruds';
import { getAbsoluteNow } from '../../../../utils/general/time';
import { checkModuleIsActive } from '../../../../utils/interaction_tools/module';
import { IInMemoryBufferConfig, InMemoryBuffer } from '../../../../utils/store/memory_buffer';
import { UnsubscribingContextManager } from '../../../../utils/store/unsubscribing_manager';
import {
  ELiveErrorCategory,
  trackLiveAction,
  withLiveErrorTracking,
} from '../../../../utils/tracking';
import {
  ELiveRealtimeSignal,
  ELiveSignal,
  TFirebaseInteractionAppReadySignalEvent,
} from '../../../signals';
import {
  answerQuestion,
  approveQuestion,
  archiveQuestion,
  closeQnASession,
  createQnASession,
  deactivateQuestion,
  deleteQuestionReply,
  dispose,
  disposeActiveSessionSubscription,
  openQnASession,
  pinQuestion,
  submitQuestion,
  submitQuestionReply,
  toggleAnonymousQuestionsState,
  toggleModerationState,
  unArchiveQuestion,
  unPinQuestion,
  voteQuestion,
} from './QnAManager.operations';
import {
  hydrateActiveSession,
  hydrateAnonymousQuestionsState,
  hydrateModerationState,
  subscribeToActiveSessionMeta,
  subscribeToAnonymousQuestionsState,
  subscribeToModerationState,
} from './QnAManager.subscription';

export interface IQnAContext {
  qnaActions: {
    createQnASession(isQnAModerated: boolean): Promise<string>;
    openQnASession(sessionId: string): Promise<void>;
    closeQnASession(): Promise<void>;
    toggleModerationState(isQnAModerated: boolean): Promise<void>;
    toggleAnonymousQuestionsState(isAnonymousQuestionsDisabled: boolean): Promise<void>;
    approveQuestion(questionId: string): Promise<void>;
    archiveQuestion(questionId: string, isApproved: boolean): void;
    unArchiveQuestion(questionId: string, isApproved: boolean): Promise<void>;
    deactivateQuestion(questionId: string): Promise<void>;
    answerQuestion(questionId: string): Promise<void>;
    submitQuestion(question: string, anonymous: boolean): Promise<void>;
    voteQuestion(questionId: string): Promise<void>;
    submitQuestionReply(questionId: string, questionReply: string): Promise<void>;
    deleteQuestionReply(questionId: string, replyId: string): Promise<void>;
    pinQuestion(questionId: string): Promise<void>;
    unPinQuestion(questionId: string): Promise<void>;
  };
  config: {
    /**
     * Whether qna moderation can be used in the current session.
     * Moderation includes following features:
     *  - pending questions and approval
     *  - anonymous questions toggling
     */
    canUseQnaModeration: Optional<boolean>;
    canViewQnaModeration: Maybe<boolean>;
  };
  isEventModerated: Optional<boolean>;
  isAnonymousQuestionsDisabled: Optional<boolean>;
  isHydrated: boolean;
  hydratedAt: Optional<number>;
  qnaSessionsHistory: Array<TInteractionHistoryConnection>;
  /**
   * Active session details.
   */
  activeSessionId: Optional<Id>;
  activeSession: Optional<IQnASession>;
  activeSessionQuestions: TQnAQuestions;
  activeSessionPendingQuestions: TQnAQuestions;
  activeSessionPinnedQuestionId: Optional<string>;
  qnaReplies: TQnAQuestionsReplies;
}

/**
 * QnA manager, subscribe/unsubscribe from polls, close-open-push polls.
 */
export class QnAManager extends UnsubscribingContextManager<IQnAContext> {
  private static createBufferConfig(
    manager: QnAManager,
    name: string,
  ): IInMemoryBufferConfig<TQnAQuestionsReplies> {
    return {
      name,
      initialValue: {},
      throttleDelay: interactionToolsConfig.QNA.QNA_REPLIES_UPDATE_THROTTLE,
      onUpdate: (value: TQnAQuestionsReplies) => {
        manager.setContext({
          qnaReplies: value,
        });
      },
    };
  }

  public override context: IQnAContext = {
    qnaActions: createActions({
      createQnASession: (isQnAModerated: boolean) => this.createQnASession(isQnAModerated),
      openQnASession: (sessionId: string) => this.openQnASession(sessionId),
      closeQnASession: () => this.closeQnASession(),
      toggleModerationState: (isQnAModerated: boolean) =>
        this.toggleModerationState(isQnAModerated),
      toggleAnonymousQuestionsState: (isAnonymousQuestionsDisabled: boolean) =>
        this.toggleAnonymousQuestionsState(isAnonymousQuestionsDisabled),
      approveQuestion: (questionId: string) => this.approveQuestion(questionId),
      archiveQuestion: (questionId: string, isApproved: boolean) =>
        this.archiveQuestion(questionId, isApproved),
      unArchiveQuestion: (questionId: string, isApproved: boolean) =>
        this.unArchiveQuestion(questionId, isApproved),
      deactivateQuestion: (questionId: string) => this.deactivateQuestion(questionId),
      answerQuestion: (questionId: string) => this.answerQuestion(questionId),
      submitQuestion: (question: string, anonymous: boolean) =>
        this.submitQuestion(question, anonymous),
      voteQuestion: (questionId: string) => this.voteQuestion(questionId),
      submitQuestionReply: (questionId: string, questionReply: string) =>
        this.submitQuestionReply(questionId, questionReply),
      deleteQuestionReply: (questionId: string, replyId: string) =>
        this.deleteQuestionReply(questionId, replyId),
      pinQuestion: (questionId: string) => this.pinQuestion(questionId),
      unPinQuestion: (questionId: string) => this.unPinQuestion(questionId),
    }),
    config: {
      canUseQnaModeration: null,
      canViewQnaModeration: null,
    },
    isHydrated: false,
    hydratedAt: null,
    isEventModerated: null,
    isAnonymousQuestionsDisabled: null,
    qnaSessionsHistory: [],
    /**
     * Active session details.
     */
    activeSessionId: null,
    activeSession: null,
    activeSessionQuestions: {},
    activeSessionPendingQuestions: {},
    activeSessionPinnedQuestionId: null,
    qnaReplies: {},
  };

  public readonly log: Logger = new Logger('🌮QNA');
  /**
   * Whether the qna module is active in the current context.
   */
  public readonly isModuleActive: boolean = false;
  public readonly composerSessionType: Definable<EComposerSessionType>;

  public firebase!: IFirebaseDependencies;
  public qnaSessionsRef: Optional<TFirebaseReference> = null;
  public qnaModerationStatusRef: Optional<TFirebaseReference> = null;
  public qnaAnonymousQuestionsStatusRef: Optional<TFirebaseReference> = null;
  public qnaActiveSessionRef: Optional<TFirebaseReference> = null;
  public qnaActiveSessionMetaRef: Optional<TFirebaseReference> = null;
  public qnaRepliesSessionsRef: Optional<TFirebaseReference> = null;
  public rootRef: Optional<TFirebaseReference> = null;

  public activeSessionUnSubscribers: Array<Callable> = [];

  public qnaRepliesBuffer: Optional<InMemoryBuffer<TQnAQuestionsReplies>> = null;

  public constructor(initialState?: IInteractionStoreInitialState) {
    super();

    this.isModuleActive = checkModuleIsActive(
      initialState?.interaction?.feature?.module,
      EInteractionModule.QNA,
    );

    this.composerSessionType = initialState?.sessionType;
  }

  public override onProvisionEnded(): void {
    return dispose(this);
  }

  /**
   * Dispose subscribers of manager and active session subscription.
   */
  public override disposeSubscribers(category?: string): void {
    super.disposeSubscribers(category);

    /**
     * If all subscribers should be removed, not depending on category.
     */
    if (!category) {
      disposeActiveSessionSubscription(this);
    }
  }

  /**
   * Create dedicated QNA session.
   */
  public async createQnASession(isQnAModerated: boolean): Promise<string> {
    return withLiveErrorTracking(() => createQnASession(this, isQnAModerated), {
      method: 'createQnASession',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Toggle moderation state for whole set of RLEs.
   */
  public async toggleModerationState(isQnAModerated: boolean): Promise<void> {
    return withLiveErrorTracking(() => toggleModerationState(this, isQnAModerated), {
      method: 'toggleModerationState',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        isQnAModerated,
        qnaModerationStatusRef: this.qnaModerationStatusRef?.toString(),
      },
    });
  }

  /**
   * Toggle anonymous questions state for whole set of RLEs.
   */
  public async toggleAnonymousQuestionsState(isAnonymousQuestionsDisabled: boolean): Promise<void> {
    return withLiveErrorTracking(
      () => toggleAnonymousQuestionsState(this, isAnonymousQuestionsDisabled),
      {
        method: 'toggleAnonymousQuestionsState',
        category: ELiveErrorCategory.INTERACTION,
        data: {
          isAnonymousQuestionsDisabled,
          qnaAnonymousQuestionsStatusRef: this.qnaAnonymousQuestionsStatusRef?.toString(),
        },
      },
    );
  }

  /**
   * Mark specific qna session as open and ready.
   */
  public async openQnASession(sessionId: string): Promise<void> {
    return withLiveErrorTracking(() => openQnASession(this, sessionId), {
      method: 'openQnASession',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        sessionId,
        qnaSessionsRef: this.qnaSessionsRef?.toString(),
      },
    });
  }

  /**
   * Close simple QNA session.
   */
  public async closeQnASession(): Promise<void> {
    return withLiveErrorTracking(() => closeQnASession(this), {
      method: 'closeQnASession',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Approve question - move it form pending to active collection.
   */
  public async approveQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => approveQuestion(this, questionId), {
      method: 'approveQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        questionId,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  /**
   * Archive specific question and remove it from pending/active collections.
   */
  public async archiveQuestion(questionId: string, isApproved: boolean): Promise<void> {
    return withLiveErrorTracking(() => archiveQuestion(this, questionId, isApproved), {
      method: 'approveQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        questionId,
        isApproved,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  public async unArchiveQuestion(questionId: string, isApproved: boolean): Promise<void> {
    return withLiveErrorTracking(() => unArchiveQuestion(this, questionId, isApproved), {
      method: 'unArchiveQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        questionId,
        isApproved,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  public async deactivateQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => deactivateQuestion(this, questionId), {
      method: 'deactivateQuestion',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Start answering question and push graphic object to collection with descriptive snapshot.
   */
  public async answerQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => answerQuestion(this, questionId), {
      method: 'answerQuestion',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Ask question in currently active session.
   */
  public async submitQuestion(question: string, isAnonymous: boolean = false): Promise<void> {
    return withLiveErrorTracking(() => submitQuestion(this, question, isAnonymous), {
      method: 'submitQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        question,
        isAnonymous,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  public async voteQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => voteQuestion(this, questionId), {
      method: 'voteQuestion',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  public async submitQuestionReply(questionId: string, questionReply: string): Promise<void> {
    return withLiveErrorTracking(() => submitQuestionReply(this, questionId, questionReply), {
      method: 'submitQuestionReply',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  public async deleteQuestionReply(questionId: string, replyId: string): Promise<void> {
    return withLiveErrorTracking(() => deleteQuestionReply(this, questionId, replyId), {
      method: 'deleteQuestionReply',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  public async pinQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => pinQuestion(this, questionId), {
      method: 'pinQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        questionId,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  public async unPinQuestion(questionId: string): Promise<void> {
    return withLiveErrorTracking(() => unPinQuestion(this, questionId), {
      method: 'unPinQuestion',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        questionId,
        qnaActiveSessionRef: this.qnaActiveSessionRef?.toString(),
      },
    });
  }

  /**
   * Assert question exists and can be modified/updated.
   */
  public assertQuestionExists(
    questionId: string,
    collection = this.context.activeSessionQuestions,
  ): IQnAQuestion | never {
    const question: Maybe<IQnAQuestion> = collection[questionId];

    if (!question) {
      throw new LiveError('Cannot modify question that does not exist.');
    }

    return question;
  }

  /**
   * Assert current manager is initialized and can continue working with current scope.
   */
  public assertIsInitialized(): asserts this is {
    qnaSessionsRef: TFirebaseReference;
    qnaActiveSessionMetaRef: TFirebaseReference;
    qnaModerationStatusRef: TFirebaseReference;
    qnaAnonymousQuestionsStatusRef: TFirebaseReference;
    rootRef: TFirebaseReference;
  } {
    if (
      !this.qnaSessionsRef ||
      !this.qnaActiveSessionMetaRef ||
      !this.qnaModerationStatusRef ||
      !this.qnaAnonymousQuestionsStatusRef
    ) {
      throw new LiveError('QnA manager is not initialized.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }
  }

  /**
   * Assert active session exists.
   */
  public assertActiveSessionExists(): asserts this is { qnaActiveSessionRef: TFirebaseReference } {
    if (!this.qnaActiveSessionRef) {
      throw new LiveError('QnA session is not initialized.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }
  }

  @OnSignal(ELiveRealtimeSignal.FIREBASE_INTERACTION_APP_READY)
  private async onChatApplicationReady({
    data: { firebase, app, roomId, user, roomHash, metadata },
  }: TFirebaseInteractionAppReadySignalEvent): Promise<void> {
    this.firebase = firebase;

    if (this.isModuleActive) {
      this.log.info('🚀Initializing qna connection:', roomId, roomHash, user);
      trackLiveAction('fb_init_qna_manager');

      this.qnaRepliesBuffer = new InMemoryBuffer<TQnAQuestionsReplies>(
        QnAManager.createBufferConfig(this, 'qnaRepliesBuffer'),
      );

      const database: TFirebaseDatabase = getRealtimeDatabase(firebase, app);

      this.rootRef = getDatabaseRootRef(firebase, database);
      this.qnaSessionsRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.QNA.SESSIONS(roomId),
      );
      this.qnaActiveSessionMetaRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.QNA.ACTIVE_ID(roomId),
      );
      this.qnaModerationStatusRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.QNA.MODERATION(roomHash),
      );
      this.qnaAnonymousQuestionsStatusRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.QNA.ANONYMOUS_QUESTIONS(roomHash),
      );
      this.qnaRepliesSessionsRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.QNA.REPLIES_SESSIONS(roomId),
      );

      await Promise.all([
        hydrateActiveSession(this, this.qnaSessionsRef, this.qnaActiveSessionMetaRef),
        hydrateModerationState(this, this.qnaModerationStatusRef),
        hydrateAnonymousQuestionsState(this, this.qnaAnonymousQuestionsStatusRef),
      ]).finally(() => {
        this.log.info('Hydration finished');
        this.setContext({
          isHydrated: true,
          hydratedAt: getAbsoluteNow(),
          qnaSessionsHistory: metadata?.connections?.questionsHistory || [],
          config: {
            canUseQnaModeration: user?.capabilities?.canUseQnaModeration,
            canViewQnaModeration: true,
          },
        });
      });

      this.addUnSubscribers([
        subscribeToActiveSessionMeta(this, this.qnaSessionsRef, this.qnaActiveSessionMetaRef),
        subscribeToModerationState(this, this.qnaModerationStatusRef),
        subscribeToAnonymousQuestionsState(this, this.qnaAnonymousQuestionsStatusRef),
      ]);
    } else {
      this.log.info('🚀Skipping qna connection');
    }
  }

  @OnSignal(ELiveSignal.INTERACTION_SESSION_LOGOUT)
  private onInteractionSessionLogout(): void {
    return dispose(this);
  }
}
